import { useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useRouter } from 'expo-router';
import Chip from '@/components/General/Chip';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { getEnrollmentStatusMeta, getStopReasonLabel } from '@/utils/constants/followUp';
import { parseTimeFrame } from '@/utils/dater';

/**
 * PersonEnrollmentsCard — a person's active + past Path enrollments on the
 * Follow Up care surface (§25.5). Each row links to the flat enrollment
 * timeline; ACTIVE enrollments sort first.
 */
const PersonEnrollmentsCard = ({ organizationID, personID }) => {
    const router = useRouter();

    const {
        data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage,
    } = Query.followUp.enrollments({
        organizationID,
        filter: { personID, status: 'ALL' },
        options: { enabled: !!organizationID && !!personID },
    });

    const enrollments = useMemo(
        () => (data?.pages || []).flatMap((page) => page?.rows || []),
        [data]
    );

    if (isLoading) {
        return (
            <View style={styles.section} testID="person-enrollments-loading">
                <Text style={styles.sectionTitle}>Paths</Text>
                <View style={styles.loadingWrap}><ActivityIndicator /></View>
            </View>
        );
    }
    if (enrollments.length === 0) return null;

    const sorted = [...enrollments].sort((a, b) => {
        const aOpen = a.status === 'ACTIVE' ? 0 : 1;
        const bOpen = b.status === 'ACTIVE' ? 0 : 1;
        return aOpen - bOpen;
    });

    return (
        <View style={styles.section} testID="person-enrollments">
            <Text style={styles.sectionTitle}>Paths</Text>
            <View style={styles.card}>
                {sorted.map((enrollment) => {
                    const meta = getEnrollmentStatusMeta(enrollment.status);
                    const current = enrollment.currentStep;
                    return (
                        <Pressable
                            key={enrollment.enrollmentID}
                            testID={`person-enrollment-${enrollment.enrollmentID}`}
                            accessibilityRole="button"
                            onPress={() => router.push(`/follow-up/enrollments/${enrollment.enrollmentID}`)}
                            style={styles.row}
                        >
                            <View style={styles.iconWrap}>
                                <Unicon name="git-branch-outline" size={15} colorVariant="paths" />
                            </View>
                            <View style={styles.rowBody}>
                                <Text style={styles.rowTitle} numberOfLines={1}>{enrollment.pathName || 'Path'}</Text>
                                <Text style={styles.rowMeta} numberOfLines={1}>
                                    {[
                                        current?.label ? `Now: ${current.label}` : null,
                                        enrollment.startedAt ? `Started ${parseTimeFrame(enrollment.startedAt)}` : null,
                                        enrollment.completedAt ? `Finished ${parseTimeFrame(enrollment.completedAt)}` : null,
                                        enrollment.stopReason ? getStopReasonLabel(enrollment.stopReason) : null,
                                    ].filter(Boolean).join(' · ') || 'Enrollment'}
                                </Text>
                            </View>
                            {!!enrollment.needsAttention && (
                                <Chip icon="alert-circle-outline" colorVariant="warning">Needs attention</Chip>
                            )}
                            <Chip icon={meta.icon} colorVariant={meta.colorVariant}>{meta.label}</Chip>
                            <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
                        </Pressable>
                    );
                })}
            </View>
            {hasNextPage && (
                <View style={styles.footer}>
                    {isFetchingNextPage ? (
                        <ActivityIndicator />
                    ) : (
                        <SolidButton
                            testID="person-enrollments-load-more"
                            variant="outlined"
                            type="secondary"
                            size="sm"
                            text="Load More"
                            onPress={() => fetchNextPage()}
                        />
                    )}
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    section: { gap: 6 },
    sectionTitle: { fontSize: 13, fontFamily: fonts.heading, color: colors.textSecondary },
    loadingWrap: { padding: 16, alignItems: 'center' },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        boxShadow: shadows.card,
        overflow: 'hidden',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        _web: { cursor: 'pointer', _hover: { backgroundColor: colors.hover } },
    },
    iconWrap: {
        width: 30,
        height: 30,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    rowBody: { flex: 1, minWidth: 0 },
    rowTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    rowMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
    footer: { alignItems: 'center', paddingVertical: 8 },
}));

export default PersonEnrollmentsCard;
